import { Component, ErrorInfo, ReactNode } from 'react';
import { captureException, addBreadcrumb } from '../../utils/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child
 * component tree and logs those errors to Sentry while displaying a fallback UI.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName = 'UnknownComponent' } = this.props;

    // Add breadcrumb for debugging context
    addBreadcrumb(`Error caught in ${componentName}`, 'error_boundary', {
      componentName,
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });

    // Capture the error with Sentry
    captureException(error, {
      errorBoundary: {
        componentName,
        componentStack: errorInfo.componentStack,
        errorStack: error.stack,
      },
      react: {
        errorInfo,
      },
    });

    // Log to console for development
    console.error(`Error in ${componentName}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            padding: '20px',
            border: '1px solid #ff6b6b',
            borderRadius: '4px',
            backgroundColor: '#fff5f5',
            color: '#c92a2a',
            textAlign: 'center',
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>
            Something went wrong
          </h3>
          <p style={{ margin: '0', opacity: 0.8 }}>
            We're sorry, but something unexpected happened. The error has been
            reported.
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: undefined })}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#c92a2a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
